/**
 * Small form controls. Number fields keep what the user typed while it is not a number
 * yet ("-", "12.") and only dispatch finite values.
 */
import { useEffect, useId, useState, type ReactNode } from 'react';

export function Field({ label, issues = [], children, wide = false }: { label: string; issues?: string[]; children: ReactNode; wide?: boolean }) {
  return (
    <label className={`field${wide ? ' wide' : ''}${issues.length ? ' invalid' : ''}`}>
      <span className="field-label">{label}</span>
      {children}
      {issues.map((m, i) => (
        <span key={i} className="field-issue">
          {m}
        </span>
      ))}
    </label>
  );
}

export function NumberInput({ value, onChange, step = 1, min, max, integer = false }: { value: number; onChange: (v: number) => void; step?: number; min?: number; max?: number; integer?: boolean }) {
  const [draft, setDraft] = useState(String(value));
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    if (!focused) setDraft(String(value));
  }, [value, focused]);
  return (
    <input
      type="number"
      inputMode="decimal"
      value={draft}
      step={step}
      min={min}
      max={max}
      onFocus={() => setFocused(true)}
      onBlur={() => {
        setFocused(false);
        setDraft(String(value));
      }}
      onChange={(e) => {
        setDraft(e.target.value);
        const n = Number(e.target.value);
        if (e.target.value.trim() !== '' && Number.isFinite(n)) onChange(integer ? Math.trunc(n) : n);
      }}
    />
  );
}

export function TextInput({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder?: string }) {
  return <input type="text" value={value} placeholder={placeholder} spellCheck={false} onChange={(e) => onChange(e.target.value)} />;
}

const ALIGN_GRID: readonly (readonly [number, string])[] = [
  [5, 'top left'], [4, 'top'], [6, 'top right'],
  [1, 'left'], [0, 'center'], [2, 'right'],
  [9, 'bottom left'], [8, 'bottom'], [10, 'bottom right'],
];

/** OBS alignment as a 3×3 picker (which point of the box sits at the position). */
export function AlignmentPicker({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const name = useId();
  return (
    <div className="align-picker" role="radiogroup">
      {ALIGN_GRID.map(([v, label]) => (
        <label key={v} className={v === value ? 'on' : ''} title={`${label} (${v})`}>
          <input type="radio" name={name} checked={v === value} onChange={() => onChange(v)} aria-label={label} />
          <span />
        </label>
      ))}
    </div>
  );
}
